import type { SortTableId, StockAdminQueue, TableSort } from '@myrewards/contracts';
import { useMemo, type RefObject } from 'react';
import { useListTable, type DataColumn, type ListTable } from '../../ui';
import {
  byAwardNumber,
  QUEUE_DEFAULT_SORT,
  QUEUE_DEFAULT_WORDS,
  queueFilterDefs,
  type QueueFilterId,
} from './sorting';

type Row = StockAdminQueue['rows'][number];

/**
 * One queue table's sort and filters (APP-149 D3, D4): kept in the address, oldest submitted first
 * by default, opening at the person's saved sort with Keep my sort (D7), applied to the whole
 * list. The Home card and every Queue page view use it, each naming its table key and the filters
 * that mean something there.
 */
export function useQueueTable({
  table,
  rows,
  columns,
  filterIds,
  meId,
  asOf,
  barRef,
  defaultSort = QUEUE_DEFAULT_SORT,
  defaultWords = QUEUE_DEFAULT_WORDS,
}: {
  table: SortTableId;
  rows: readonly Row[];
  /** The table's columns; those with `sort` are the ones that sort. */
  columns: readonly DataColumn<Row>[];
  filterIds: readonly QueueFilterId[];
  meId: string;
  asOf: string;
  /** The FilterBar's wrapper: "Clear filters" in an empty table lands focus on its first filter. */
  barRef: RefObject<HTMLElement | null>;
  /** The order it opens in; oldest first unless the page says otherwise (the Queue page, APP-244). */
  defaultSort?: TableSort;
  defaultWords?: string;
}): ListTable<Row, QueueFilterId> {
  const filters = useMemo(
    () => queueFilterDefs(rows, filterIds, { meId, asOf }),
    [rows, filterIds, meId, asOf],
  );
  return useListTable<Row, QueueFilterId>({
    table,
    rows,
    columns,
    defaultSort,
    defaultWords,
    filters,
    tieBreak: byAwardNumber,
    barRef,
  });
}
